import { useEffect, useMemo } from "react";
import type { InviteType } from "@ano/shared/api/invites";
import {
  deriveGuestEligibility,
  readGuestInviteTokenFromSearch,
} from "./auth-guest-branch";

export interface GuestInvite {
  guestInviteToken: string | null;
  guestInviteType: InviteType | null;
  isGuestEligible: boolean;
}

/**
 * Guest DM access — parse the invite token for unauthenticated guest mode and
 * persist it across the OAuth redirect.
 *
 * The memo + eligibility derivation drives the AuthGate render ladder
 * (GuestMode for DM/group-DM invites, InviteLoginPage otherwise). The effect
 * mirrors the token into localStorage with a timestamp so it survives the
 * round-trip through WorkOS; stale tokens expire after 1 hour (consumer side).
 *
 * Extracted verbatim from AuthGate; the `useMemo` (`[]`) and `useEffect`
 * (`[guestInviteToken]`) dependency arrays are preserved exactly.
 */
export function useGuestInvite(): GuestInvite {
  const guestInviteToken = useMemo(() => {
    return readGuestInviteTokenFromSearch(window.location.search);
  }, []);
  const { guestInviteType, isGuestEligible } =
    deriveGuestEligibility(guestInviteToken);

  // Persist invite token to localStorage so it survives OAuth redirects.
  // Store with timestamp so stale tokens expire after 1 hour.
  useEffect(() => {
    if (guestInviteToken) {
      try {
        localStorage.setItem(
          "ano-pending-invite-token",
          JSON.stringify({ token: guestInviteToken, ts: Date.now() }),
        );
      } catch {
        // ignore — localStorage may be unavailable
      }
    }
  }, [guestInviteToken]);

  return { guestInviteToken, guestInviteType, isGuestEligible };
}
